<script setup>
import {
  ref,
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import pipelineAutomationsAPI from 'dashboard/api/pipelineAutomations';
import { usePipelinePermissions } from '../../../composables/usePipelinePermissions';
import {
  getTriggerOptions,
  eventTypeForTriggerSubtype,
} from '../../composables/nodeCatalog';
import {
  getAvailableTokens,
  wrapToken,
  CONDITION_FIELD_GROUPS,
  CONDITION_FIELD_TYPES,
} from '../../composables/expressionTokens';
import { contextsFromFlow } from '../../utils/flowCapabilities';
import {
  normalizeConditionField,
  normalizeTriggerFilterData,
  normalizeTriggerSubtype,
} from '../../utils/triggerFilterContract';

const props = defineProps({
  nodeId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['close', 'save']);
const store = useStore();
const { t } = useI18n();
const { canManageAutomations } = usePipelinePermissions();

// ─── Local form state ────────────────────────────────────────────────────────

const localData = ref({
  // Shared
  type: '',
  eventType: '',
  description: '',
  // Trigger
  pipelineId: '',
  pattern: '',
  keyword: '',
  fromColumn: '',
  toColumn: '',
  stage_from: '',
  stage_to: '',
  schedule: 'daily',
  time: '',
  // Condition
  field: '',
  operator: 'equals',
  value: '',
  // Action – messaging
  message: '',
  content: '',
  notificationTitle: '',
  title: '',
  // Action – agent/team/label
  agentId: '',
  agent_id: '',
  teamId: '',
  team_id: '',
  labelId: '',
  label: '',
  // Action – status / priority
  status: 'open',
  priority: 'none',
  // Action – wait
  duration: 1,
  unit: 'minutes',
  // Action – http_request
  url: '',
  method: 'POST',
  headers: '',
  body: '',
  // Action – captain
  captainAssistantId: '',
  captainInput: '',
  input: '',
  // Action – google calendar
  eventTitle: '',
  eventDuration: 60,
  eventDescription: '',
  duration_minutes: 60,
  // Action – move_to_stage
  targetPipelineId: '',
  stageId: '',
  stage: '',
  // Misc
  target: '',
  // Action – email
  subject: '',
  emailAddress: '',
  // Action – media / attachment
  mediaUrl: '',
  attachmentUrl: '',
  // Action – snooze
  snoozeDuration: 1,
  snoozeUnit: 'hours',
  // Action – set_attribute / update_contact
  attributeKey: '',
  attributeValue: '',
  contactField: '',
  contactValue: '',
  // Action – add_tag
  tag: '',
  // Action – create_pipeline_card
  cardPipelineId: '',
  cardTitle: '',
  // Action – create_task
  taskTitle: '',
  taskDescription: '',
  taskDueDate: '',
});

const errors = ref({});
const showVariablePicker = ref(false);
const activeVarField = ref('message');

// ─── set_variable editor state ────────────────────────────────────────────
// Working list synced into localData.variables on each mutation. Starts
// from the loaded node's data.variables (or a single empty pair).
const setVariableEntries = ref([{ name: '', value: '' }]);
const syncSetVariableEntries = () => {
  localData.value.variables = setVariableEntries.value
    .filter(e => e.name?.trim())
    .map(e => ({ name: e.name, value: e.value }));
};
const addSetVariableEntry = () => {
  if (setVariableEntries.value.length >= 20) return;
  setVariableEntries.value.push({ name: '', value: '' });
};
const removeSetVariableEntry = idx => {
  setVariableEntries.value.splice(idx, 1);
  if (setVariableEntries.value.length === 0) {
    setVariableEntries.value.push({ name: '', value: '' });
  }
  syncSetVariableEntries();
};

// ─── http_response editor state ───────────────────────────────────────────
// Body and headers live as raw JSON strings in the editor (UX: monospace
// freeform editing). Serialized to objects in serializableData() below.
const httpResponseBodyParseError = ref('');
const httpResponseHeadersParseError = ref('');
const httpResponseBodyPlaceholder =
  '{\n  "order_id": "{{ webhook_payload.order_id }}",\n  "status": "ok"\n}';
const httpResponseHeadersPlaceholder = '{"X-Source": "flow-builder"}';

// ─── switch editor state ──────────────────────────────────────────────────
// Mirrors PipelineAutomations::FlowControl::SwitchHandler::SUPPORTED_OPERATORS
const SWITCH_OPERATORS = [
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'greater_than',
  'less_than',
  'greater_or_equal',
  'less_or_equal',
  'matches_regex',
  'is_empty',
  'is_not_empty',
  'in_list',
  'not_in_list',
];
const switchCases = ref([{ id: 'case_1', operator: 'equals', value: '' }]);
const syncSwitchCases = () => {
  localData.value.cases = switchCases.value.map((c, idx) => ({
    id: c.id || `case_${idx + 1}`,
    operator: c.operator || 'equals',
    value: c.value || '',
  }));
};
const addSwitchCase = () => {
  if (switchCases.value.length >= 20) return;
  const nextId = `case_${switchCases.value.length + 1}_${Date.now().toString(36)}`;
  switchCases.value.push({ id: nextId, operator: 'equals', value: '' });
  syncSwitchCases();
};
const removeSwitchCase = idx => {
  switchCases.value.splice(idx, 1);
  if (switchCases.value.length === 0) {
    switchCases.value.push({ id: 'case_1', operator: 'equals', value: '' });
  }
  syncSwitchCases();
};

const initialDraftSnapshot = ref(null);
const discardConfirmation = ref(null);
const webhookRotationConfirmation = ref(null);
const captureDraftSnapshot = () =>
  JSON.stringify({
    localData: localData.value,
    setVariableEntries: setVariableEntries.value,
    switchCases: switchCases.value,
  });
const isDirty = computed(
  () =>
    initialDraftSnapshot.value !== null &&
    captureDraftSnapshot() !== initialDraftSnapshot.value
);

// ─── Store getters ────────────────────────────────────────────────────────────

const accountId = computed(() => store.getters.getCurrentAccountId);
const agents = computed(() => store.getters['agents/getAgents'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);
const labels = computed(() => store.getters['labels/getLabels'] || []);
const captainAssistants = computed(
  () => store.getters['captainAssistants/getRecords'] || []
);
const pipelines = computed(() => store.getters['pipelines/getPipelines'] || []);
// Automations the user can call from a `call_automation` node. The current
// automation may appear here too; the backend has loop protection, so a
// self/cyclic call is rejected server-side rather than hidden in the UI.
const automations = computed(
  () => store.getters['automationFlow/getAutomations'] || []
);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const channelTypeOptions = computed(() => {
  const channelTypes = new Set(
    inboxes.value.map(inbox => inbox.channel_type).filter(Boolean)
  );
  return [...channelTypes].sort().map(value => ({
    value,
    label: value.replace(/^Channel::/, ''),
  }));
});

const node = computed(() => {
  if (!props.nodeId) return null;
  return store.state.automationFlow.flow.nodes.find(n => n.id === props.nodeId);
});
const flowContexts = computed(() =>
  contextsFromFlow(store.state.automationFlow.flow.nodes)
);

const nodeType = computed(() => node.value?.type || '');
const actionType = computed(() => node.value?.data?.type || '');
const triggerType = computed(
  () => localData.value.eventType || node.value?.data?.type || ''
);

// Trigger dropdown options derived from the node catalog (single source) so the
// editor always offers exactly what the toolbar can create — and persists the
// canonical event_type the backend matches on (audit P1-1 / A5 appointments).
const triggerOptions = computed(() => getTriggerOptions(t));

// Stages derived from the pipeline selected in the trigger
const triggerPipelineStages = computed(() => {
  const pid = String(localData.value.pipelineId || '');
  if (!pid) return [];
  const pipeline = pipelines.value.find(p => String(p.id) === pid);
  if (!pipeline?.stages) return [];
  return Object.entries(pipeline.stages).map(([key, val]) => ({
    id: key,
    name: val.name || key,
  }));
});

// Stages for the move_to_stage / move_item action
const actionPipelineStages = computed(() => {
  const pid = String(localData.value.targetPipelineId || '');
  if (!pid) return [];
  const pipeline = pipelines.value.find(p => String(p.id) === pid);
  if (!pipeline?.stages) return [];
  return Object.entries(pipeline.stages).map(([key, val]) => ({
    id: key,
    name: val.name || key,
  }));
});

// Webhook credentials never travel in the regular automation payload. An
// administrator fetches the URL only while this editor is open.
const webhookUrl = ref('');
const webhookCredentialsLoading = ref(false);
const webhookCredentialError = ref(false);
const webhookRotationLoading = ref(false);
let webhookCredentialGeneration = 0;

const normalizedAccountId = () => {
  const currentId = accountId.value;
  if (currentId === undefined || currentId === null) return null;
  return String(currentId).trim() || null;
};

const captureWebhookCredentialContext = () => ({
  accountId: normalizedAccountId(),
  automationId: store.state.automationFlow.currentAutomation?.id ?? null,
  generation: webhookCredentialGeneration,
});

const webhookCredentialContextIsCurrent = context =>
  context.accountId !== null &&
  context.accountId === normalizedAccountId() &&
  String(context.automationId ?? '') ===
    String(store.state.automationFlow.currentAutomation?.id ?? '') &&
  context.generation === webhookCredentialGeneration;

const clearWebhookCredentialState = () => {
  webhookCredentialGeneration += 1;
  webhookUrl.value = '';
  webhookCredentialError.value = false;
  webhookCredentialsLoading.value = false;
  webhookRotationLoading.value = false;
};

const loadWebhookCredentials = async () => {
  clearWebhookCredentialState();
  const context = captureWebhookCredentialContext();
  const automation = store.state.automationFlow.currentAutomation;

  if (
    context.accountId === null ||
    !canManageAutomations.value ||
    triggerType.value !== 'webhook_received' ||
    !automation?.id ||
    !automation.webhook_configured
  ) {
    webhookCredentialsLoading.value = false;
    return;
  }

  webhookCredentialsLoading.value = true;
  try {
    const { data } = await pipelineAutomationsAPI.webhookCredentials(
      context.automationId,
      context.accountId
    );
    if (webhookCredentialContextIsCurrent(context)) {
      webhookUrl.value = data?.webhook_url || '';
    }
  } catch {
    if (webhookCredentialContextIsCurrent(context)) {
      webhookCredentialError.value = true;
    }
  } finally {
    if (webhookCredentialContextIsCurrent(context)) {
      webhookCredentialsLoading.value = false;
    }
  }
};

const rotateWebhookToken = async () => {
  const confirmationContext = captureWebhookCredentialContext();
  if (
    confirmationContext.accountId === null ||
    !confirmationContext.automationId ||
    !canManageAutomations.value
  ) {
    return;
  }
  if (!(await webhookRotationConfirmation.value.showConfirmation())) return;
  if (!webhookCredentialContextIsCurrent(confirmationContext)) return;

  webhookCredentialGeneration += 1;
  const requestContext = captureWebhookCredentialContext();
  webhookRotationLoading.value = true;
  webhookCredentialError.value = false;
  try {
    const { data } = await pipelineAutomationsAPI.rotateWebhookToken(
      requestContext.automationId,
      requestContext.accountId
    );
    if (!webhookCredentialContextIsCurrent(requestContext)) return;

    webhookUrl.value = data?.webhook_url || '';
  } catch {
    if (webhookCredentialContextIsCurrent(requestContext)) {
      webhookCredentialError.value = true;
    }
  } finally {
    if (webhookCredentialContextIsCurrent(requestContext)) {
      webhookRotationLoading.value = false;
    }
  }
};

watch(
  [
    triggerType,
    canManageAutomations,
    accountId,
    () => store.state.automationFlow.currentAutomation?.id,
    () => store.state.automationFlow.currentAutomation?.webhook_configured,
  ],
  loadWebhookCredentials,
  { immediate: true, flush: 'sync' }
);

onBeforeUnmount(clearWebhookCredentialState);

// ─── Operators filtered by field type ────────────────────────────────────────

const availableOperators = computed(() => {
  const ft = CONDITION_FIELD_TYPES[localData.value.field] || 'string';
  if (
    ft === 'enum_status' ||
    ft === 'enum_priority' ||
    ft === 'agent' ||
    ft === 'team' ||
    ft === 'channel'
  ) {
    return ['equals', 'not_equals'];
  }
  if (ft === 'enum_set') {
    return ['equals', 'not_equals', 'is_empty', 'is_not_empty'];
  }
  return ['equals', 'not_equals', 'contains', 'not_contains'];
});

const conditionValueType = computed(() => {
  return CONDITION_FIELD_TYPES[localData.value.field] || 'string';
});

// ─── Title ───────────────────────────────────────────────────────────────────

const title = computed(() => {
  if (nodeType.value === 'trigger')
    return t('PIPELINE.FLOW_BUILDER.EDITOR.TITLE_TRIGGER');
  if (nodeType.value === 'condition')
    return t('PIPELINE.FLOW_BUILDER.EDITOR.TITLE_CONDITION');
  if (nodeType.value === 'action')
    return t('PIPELINE.FLOW_BUILDER.EDITOR.TITLE_ACTION');
  return t('PIPELINE.FLOW_BUILDER.EDITOR.TITLE_NODE', 'Configurar Nó');
});

const editorId = computed(() => {
  const safeNodeId = String(props.nodeId || 'new').replace(
    /[^a-zA-Z0-9_-]/g,
    '-'
  );
  return `flow-node-editor-${safeNodeId}`;
});

const controlId = field => `${editorId.value}-${field}`;

// ─── Load data ────────────────────────────────────────────────────────────────

const isLoadingNodeData = ref(false);

const loadNodeData = () => {
  if (!node.value) return;
  const loadedNodeId = props.nodeId;
  initialDraftSnapshot.value = null;
  isLoadingNodeData.value = true;
  const d = node.value.data || {};
  errors.value = {};
  // Purge all keys from the previous node before repopulating so that
  // type-specific fields from a prior node cannot bleed into this one.
  Object.keys(localData.value).forEach(key => {
    delete localData.value[key];
  });
  localData.value = {
    type: d.type || '',
    // localData.eventType holds the UI subtype. The compatibility resolver
    // understands legacy generic event nodes and the renamed pipeline-move
    // event, so an old flow never opens with a blank trigger selector.
    eventType: normalizeTriggerSubtype(d),
    description: d.description || '',
    pipelineId: d.pipeline_id ?? d.pipelineId ?? d.funnel_id ?? '',
    // keyword / pattern (both supported)
    pattern: d.pattern ?? d.keyword ?? '',
    keyword: d.keyword ?? d.pattern ?? '',
    fromColumn: d.from_stage_id ?? d.fromColumn ?? d.stage_from ?? '',
    toColumn: d.to_stage_id ?? d.toColumn ?? d.stage_to ?? '',
    stage_from: d.from_stage_id ?? d.stage_from ?? d.fromColumn ?? '',
    stage_to: d.to_stage_id ?? d.stage_to ?? d.toColumn ?? '',
    schedule: d.schedule || d.frequency || 'daily',
    time: d.time || '',
    field: normalizeConditionField(d.field) || '',
    operator: d.operator || 'equals',
    value: d.value ?? '',
    // message / content (both supported)
    message: d.message || d.content || '',
    content: d.content || d.message || '',
    notificationTitle: d.notificationTitle || d.title || '',
    title: d.title || d.notificationTitle || '',
    agentId: d.agentId || d.agent_id || '',
    agent_id: d.agent_id || d.agentId || '',
    teamId: d.teamId || d.team_id || '',
    team_id: d.team_id || d.teamId || '',
    labelId: d.labelId || d.label || '',
    label: d.label || d.labelId || '',
    status: d.status || (d.type === 'http_response' ? 200 : 'open'),
    priority: d.priority || 'none',
    duration: d.duration || 1,
    unit: d.unit || 'minutes',
    url: d.url || '',
    method: d.method || 'POST',
    headers: d.headers || '',
    body: d.body || '',
    captainAssistantId: d.captainAssistantId || '',
    captainInput: d.captainInput || d.input || '',
    input: d.input || d.captainInput || '',
    eventTitle: d.eventTitle || d.title || '',
    eventDuration: d.eventDuration || d.duration_minutes || 60,
    eventDescription: d.eventDescription || '',
    duration_minutes: d.duration_minutes || d.eventDuration || 60,
    targetPipelineId: d.targetPipelineId || '',
    stageId: d.stageId || d.stage || '',
    stage: d.stage || d.stageId || '',
    target: d.target || '',
    subject: d.subject || '',
    emailAddress: d.emailAddress || d.email || '',
    mediaUrl: d.mediaUrl || d.url || '',
    attachmentUrl: d.attachmentUrl || d.url || '',
    snoozeDuration: d.snoozeDuration || 1,
    snoozeUnit: d.snoozeUnit || 'hours',
    attributeKey: d.attributeKey || d.key || '',
    attributeValue: d.attributeValue || d.value || '',
    contactField: d.contactField || '',
    contactValue: d.contactValue || '',
    tag: d.tag || '',
    // call_automation: which automation to invoke + whether to forward vars
    automationId: d.automationId || d.automation_id || '',
    passVariables: d.passVariables ?? d.pass_variables ?? true,
    cardPipelineId: d.cardPipelineId || '',
    cardTitle: d.cardTitle || '',
    taskTitle: d.taskTitle || d.title || '',
    taskDescription: d.taskDescription || d.description || '',
    taskDueDate: d.taskDueDate || '',
    // set_variable: working list mirrored from data.variables
    variables: Array.isArray(d.variables) ? d.variables : [],
    // http_response: status + JSON-string body/headers (parsed on save)
    bodyJson:
      typeof d.body === 'object' && d.body !== null
        ? JSON.stringify(d.body, null, 2)
        : d.bodyJson || d.body || '',
    headersJson:
      typeof d.headers === 'object' && d.headers !== null
        ? JSON.stringify(d.headers, null, 2)
        : d.headersJson || d.headers || '',
    // switch: cases array as-is
    cases: Array.isArray(d.cases) ? d.cases : [],
  };

  // Initialize set_variable working list from saved data. Pick the first
  // non-empty source from: variables[] (new shape), single {name,value}
  // (legacy shape), empty placeholder. Avoids no-nested-ternary lint.
  if (d.type === 'set_variable') {
    let seed = [{ name: '', value: '' }];
    if (Array.isArray(d.variables) && d.variables.length) {
      seed = d.variables;
    } else if (d.name) {
      seed = [{ name: d.name, value: d.value || '' }];
    }
    setVariableEntries.value = seed.map(e => ({
      name: e.name || '',
      value: e.value || '',
    }));
  }

  // Initialize switch cases list
  if (node.value?.type === 'switch') {
    const seed =
      Array.isArray(d.cases) && d.cases.length
        ? d.cases
        : [{ id: 'case_1', operator: 'equals', value: '' }];
    switchCases.value = seed.map((c, idx) => ({
      id: c.id || `case_${idx + 1}`,
      operator: c.operator || 'equals',
      value: c.value ?? '',
    }));
  }

  // Reset http_response parse error on load
  httpResponseBodyParseError.value = '';
  httpResponseHeadersParseError.value = '';
  isLoadingNodeData.value = false;
  nextTick(() => {
    if (props.nodeId === loadedNodeId) {
      initialDraftSnapshot.value = captureDraftSnapshot();
    }
  });
};

onMounted(() => {
  // Load resource lists once
  const requestAccountId = accountId.value;
  if (requestAccountId) {
    if (!agents.value.length) store.dispatch('agents/get');
    if (!teams.value.length) store.dispatch('teams/get');
    if (!labels.value.length) store.dispatch('labels/get');
    if (!captainAssistants.value.length)
      store.dispatch('captainAssistants/get');
    if (!inboxes.value.length) store.dispatch('inboxes/get');
    if (!pipelines.value.length) {
      store.dispatch('pipelines/fetch', { accountId: requestAccountId });
    }
    if (!automations.value.length)
      store.dispatch('automationFlow/fetchAutomations');
  }
  loadNodeData();
});

watch(() => props.nodeId, loadNodeData);

// Keep dual-named fields in sync as user types
watch(
  () => localData.value.fromColumn,
  v => {
    localData.value.stage_from = v;
  }
);
watch(
  () => localData.value.toColumn,
  v => {
    localData.value.stage_to = v;
  }
);
watch(
  () => localData.value.message,
  v => {
    localData.value.content = v;
  }
);
watch(
  () => localData.value.keyword,
  v => {
    localData.value.pattern = v;
  }
);

// Reset operator when field changes
watch(
  () => localData.value.field,
  () => {
    if (isLoadingNodeData.value) return;
    if (!availableOperators.value.includes(localData.value.operator)) {
      localData.value.operator = availableOperators.value[0];
    }
    localData.value.value = '';
  },
  { flush: 'sync' }
);

// ─── Variable picker ─────────────────────────────────────────────────────────

const VARIABLE_CATEGORIES = computed(() =>
  getAvailableTokens(flowContexts.value).map(category => ({
    label: t(category.labelKey),
    variables: category.tokens.map(item => ({
      label: t(item.labelKey),
      value: wrapToken(item.token),
    })),
  }))
);

const openVariablePicker = fieldName => {
  activeVarField.value = fieldName;
  showVariablePicker.value = !showVariablePicker.value;
};

const insertVariable = variable => {
  const current = localData.value[activeVarField.value] || '';
  localData.value[activeVarField.value] = current + variable;
  showVariablePicker.value = false;
};

// ─── Validation ──────────────────────────────────────────────────────────────

const validate = () => {
  const e = {};
  if (nodeType.value === 'action') {
    if (
      ['send_message', 'add_note'].includes(actionType.value) &&
      !localData.value.message.trim()
    ) {
      e.message = true;
    }
    if (
      actionType.value === 'send_notification' &&
      !localData.value.message.trim()
    ) {
      e.message = true;
    }
    if (actionType.value === 'assign_agent' && !localData.value.agentId) {
      e.agentId = true;
    }
    if (actionType.value === 'assign_team' && !localData.value.teamId) {
      e.teamId = true;
    }
    if (
      ['add_label', 'remove_label'].includes(actionType.value) &&
      !localData.value.labelId
    ) {
      e.labelId = true;
    }
    if (
      ['http_request', 'send_webhook', 'trigger_webhook'].includes(
        actionType.value
      ) &&
      !localData.value.url.trim()
    ) {
      e.url = true;
    }
    if (
      actionType.value === 'wait' &&
      (!localData.value.duration || localData.value.duration < 1)
    ) {
      e.duration = true;
    }
    if (
      actionType.value === 'create_google_event' &&
      !localData.value.eventTitle.trim()
    ) {
      e.eventTitle = true;
    }
  }
  if (nodeType.value === 'condition') {
    const normalizedField = normalizeConditionField(localData.value.field);
    if (
      !normalizedField ||
      !Object.prototype.hasOwnProperty.call(
        CONDITION_FIELD_TYPES,
        normalizedField
      )
    ) {
      e.field = true;
    }
    if (!localData.value.value && !localData.value.operator.startsWith('is_'))
      e.value = true;
  }
  errors.value = e;
  return Object.keys(e).length === 0;
};

// ─── Save ────────────────────────────────────────────────────────────────────

const requestClose = async () => {
  if (!isDirty.value) return true;
  return discardConfirmation.value.showConfirmation();
};

const close = async () => {
  if (!(await requestClose())) return;
  showVariablePicker.value = false;
  clearWebhookCredentialState();
  emit('close');
};

defineExpose({ requestClose, isDirty });

const buildLabel = (type, data) => {
  if (nodeType.value === 'trigger') {
    const triggerLabels = {
      message_received: t(
        'PIPELINE.FLOW_BUILDER.NODE.TRIGGER.message_received'
      ),
      item_created: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.item_created'),
      conversation_created: t(
        'PIPELINE.FLOW_BUILDER.NODE.TRIGGER.conversation_created'
      ),
      stage_changed: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.item_moved'),
      item_moved: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.item_moved'),
      conversation_resolved: t(
        'PIPELINE.FLOW_BUILDER.NODE.TRIGGER.conversation_resolved'
      ),
      contact_created: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.contact_created'),
      first_reply_sent: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.first_reply'),
      item_won: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.item_won'),
      scheduled: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.scheduled'),
      webhook_received: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.webhook'),
    };
    return triggerLabels[type] || type;
  }

  if (nodeType.value === 'condition') {
    const field = data.field || '';
    const op = data.operator || '';
    const val = data.value || '';
    return (
      [field, op, val].filter(Boolean).join(' ') ||
      t('PIPELINE.FLOW_BUILDER.NODE.CONDITION.LABEL')
    );
  }

  if (nodeType.value === 'action') {
    const base = t(`PIPELINE.FLOW_BUILDER.NODE.ACTION.${type}`, type);
    if (data.message) {
      const preview = data.message.substring(0, 24);
      return `${base}: ${preview}${data.message.length > 24 ? '…' : ''}`;
    }
    if (data.agentId) {
      const agent = agents.value.find(
        a => String(a.id) === String(data.agentId)
      );
      if (agent) return `${base}: ${agent.name}`;
    }
    if (data.teamId) {
      const team = teams.value.find(
        tm => String(tm.id) === String(data.teamId)
      );
      if (team) return `${base}: ${team.name}`;
    }
    if (data.labelId) {
      const label = labels.value.find(
        lb => lb.id === data.labelId || lb.title === data.labelId
      );
      if (label) return `${base}: ${label.title}`;
    }
    if (data.status) return `${base}: ${data.status}`;
    if (data.duration) return `${base}: ${data.duration} ${data.unit}`;
    if (data.url) return `${base}: ${data.url.substring(0, 24)}`;
    if (data.eventTitle) return `${base}: ${data.eventTitle}`;
    return base;
  }

  return type;
};

const saveChanges = () => {
  if (!validate()) return;
  if (!node.value) return;

  const originalType = node.value.data?.type || '';
  const resolvedType =
    nodeType.value === 'trigger'
      ? localData.value.eventType || originalType
      : originalType;

  localData.value.type = resolvedType;

  // Build a type-specific data object to avoid contaminating nodes with
  // irrelevant fields from other node types (e.g. trigger getting agentId).
  const buildTypeSpecificData = () => {
    const d = localData.value;

    const base = {
      type: d.type || d.eventType,
      description: d.description,
    };

    if (nodeType.value === 'trigger') {
      return {
        ...base,
        // Map the UI subtype (d.eventType) to the canonical event_type the
        // backend matches on, using the same catalog the toolbar uses — so an
        // edited trigger persists identical JSON to a toolbar-created one and
        // never goes dead (audit P1-1). Subtypes with no dispatched event
        // (scheduled/webhook) keep their subtype as the event_type.
        event_type: eventTypeForTriggerSubtype(d.eventType) || d.eventType,
        pipelineId: d.pipelineId,
        pattern: d.pattern,
        keyword: d.keyword,
        fromColumn: d.fromColumn,
        toColumn: d.toColumn,
        stage_from: d.stage_from,
        stage_to: d.stage_to,
        schedule: d.schedule,
        time: d.time,
      };
    }

    if (nodeType.value === 'condition') {
      return {
        ...base,
        field: normalizeConditionField(d.field),
        operator: d.operator,
        value: d.value,
      };
    }

    // Action nodes – include only fields relevant to the specific actionType
    const actionBase = { ...base };

    switch (actionType.value) {
      case 'send_message':
      case 'add_note':
        return { ...actionBase, message: d.message, content: d.content };
      case 'send_notification':
        return {
          ...actionBase,
          message: d.message,
          content: d.content,
          notificationTitle: d.notificationTitle,
          title: d.title,
        };
      case 'assign_agent':
        return { ...actionBase, agentId: d.agentId, agent_id: d.agent_id };
      case 'assign_team':
        return { ...actionBase, teamId: d.teamId, team_id: d.team_id };
      case 'add_label':
      case 'remove_label':
        return { ...actionBase, labelId: d.labelId, label: d.label };
      case 'change_status':
        return { ...actionBase, status: d.status };
      case 'change_priority':
        return { ...actionBase, priority: d.priority };
      case 'wait':
        return { ...actionBase, duration: d.duration, unit: d.unit };
      case 'http_request':
      case 'send_webhook':
      case 'trigger_webhook':
        return {
          ...actionBase,
          url: d.url,
          method: d.method,
          headers: d.headers,
          body: d.body,
        };
      case 'call_captain_ai':
        return {
          ...actionBase,
          captainAssistantId: d.captainAssistantId,
          captainInput: d.captainInput,
          input: d.input,
        };
      case 'create_google_event':
        return {
          ...actionBase,
          eventTitle: d.eventTitle,
          eventDuration: d.eventDuration,
          eventDescription: d.eventDescription,
          duration_minutes: d.duration_minutes,
        };
      case 'move_to_stage':
      case 'move_item':
      case 'move_to_column':
        return {
          ...actionBase,
          targetPipelineId: d.targetPipelineId,
          stageId: d.stageId,
          stage: d.stage,
        };
      case 'transfer_to_inbox':
        return { ...actionBase, target: d.target };
      case 'send_whatsapp_template':
        return {
          ...actionBase,
          content: d.content,
          templateName: d.content,
        };
      case 'send_whatsapp_media':
        return { ...actionBase, mediaUrl: d.mediaUrl, message: d.message };
      case 'send_attachment':
        return { ...actionBase, attachmentUrl: d.attachmentUrl };
      case 'send_email_to_team':
        return {
          ...actionBase,
          teamId: d.teamId,
          subject: d.subject,
          message: d.message,
          content: d.content,
        };
      case 'send_email_transcript':
        return { ...actionBase, emailAddress: d.emailAddress };
      case 'snooze_conversation':
        return {
          ...actionBase,
          snoozeDuration: d.snoozeDuration,
          snoozeUnit: d.snoozeUnit,
        };
      case 'set_conversation_attribute':
      case 'set_attribute':
        return {
          ...actionBase,
          attributeKey: d.attributeKey,
          attributeValue: d.attributeValue,
        };
      case 'update_contact':
        return {
          ...actionBase,
          contactField: d.contactField,
          contactValue: d.contactValue,
        };
      case 'create_pipeline_card':
        return {
          ...actionBase,
          cardPipelineId: d.cardPipelineId,
          cardTitle: d.cardTitle,
        };
      case 'create_task':
        return {
          ...actionBase,
          taskTitle: d.taskTitle,
          taskDescription: d.taskDescription,
          taskDueDate: d.taskDueDate,
        };
      case 'add_tag':
        return { ...actionBase, tag: d.tag };
      case 'call_automation':
        return {
          ...actionBase,
          automationId: d.automationId,
          // Backend CallAutomationAction reads params[:automation_id] and
          // params[:pass_variables]; persist snake_case alongside camelCase.
          automation_id: d.automationId,
          pass_variables: d.passVariables,
        };
      case 'set_variable':
        // Pass through the variables array as authored by the editor.
        // Backend SetVariableAction accepts {variables: [...]} or {name, value}.
        return {
          ...actionBase,
          variables: Array.isArray(d.variables) ? d.variables : [],
        };
      case 'http_response': {
        let parsedBody = {};
        let parsedHeaders = {};
        let hasParseError = false;
        httpResponseBodyParseError.value = '';
        httpResponseHeadersParseError.value = '';
        if (d.bodyJson && d.bodyJson.trim()) {
          try {
            parsedBody = JSON.parse(d.bodyJson);
          } catch {
            httpResponseBodyParseError.value = t(
              'PIPELINE.FLOW_BUILDER.EDITOR.HTTP_RESPONSE_INVALID_JSON'
            );
            hasParseError = true;
          }
        }
        if (d.headersJson && d.headersJson.trim()) {
          try {
            parsedHeaders = JSON.parse(d.headersJson);
            if (
              !parsedHeaders ||
              Array.isArray(parsedHeaders) ||
              typeof parsedHeaders !== 'object'
            ) {
              throw new TypeError('Headers must be an object');
            }
          } catch {
            httpResponseHeadersParseError.value = t(
              'PIPELINE.FLOW_BUILDER.EDITOR.HTTP_RESPONSE_INVALID_HEADERS_JSON'
            );
            hasParseError = true;
          }
        }
        if (hasParseError) return null;
        return {
          ...actionBase,
          status: d.status || 200,
          body: parsedBody,
          headers: parsedHeaders,
        };
      }
      default:
        // resolve, reopen, mute, unmute, send_csat_survey, etc.
        return actionBase;
    }
  };

  // Switch nodes are nodeType === 'switch' (not 'action'); they have
  // input + cases. Build their data separately.
  if (nodeType.value === 'switch') {
    const d = localData.value;
    const switchData = {
      type: 'switch',
      input: d.input || '',
      cases: Array.isArray(d.cases) ? d.cases : [],
      description: d.description,
    };
    store.commit('automationFlow/updateNode', {
      nodeId: node.value.id,
      updates: { data: switchData },
      addToHistory: true,
    });
    initialDraftSnapshot.value = captureDraftSnapshot();
    emit('save');
    return;
  }

  const dataToSave = buildTypeSpecificData();
  if (!dataToSave) return;

  // Sync dual-named fields so both old and new keys are persisted
  dataToSave.content = dataToSave.content || dataToSave.message;
  dataToSave.message = dataToSave.message || dataToSave.content;
  dataToSave.keyword = dataToSave.keyword || dataToSave.pattern;
  dataToSave.pattern = dataToSave.pattern || dataToSave.keyword;
  dataToSave.stage_from = dataToSave.stage_from || dataToSave.fromColumn;
  dataToSave.fromColumn = dataToSave.fromColumn || dataToSave.stage_from;
  dataToSave.stage_to = dataToSave.stage_to || dataToSave.toColumn;
  dataToSave.toColumn = dataToSave.toColumn || dataToSave.stage_to;
  dataToSave.agent_id = dataToSave.agent_id || dataToSave.agentId;
  dataToSave.agentId = dataToSave.agentId || dataToSave.agent_id;
  dataToSave.team_id = dataToSave.team_id || dataToSave.teamId;
  dataToSave.teamId = dataToSave.teamId || dataToSave.team_id;
  dataToSave.labelSlug = dataToSave.label || dataToSave.labelId;
  dataToSave.labelId = dataToSave.labelId || dataToSave.labelSlug;
  dataToSave.label = dataToSave.labelSlug;
  dataToSave.input = dataToSave.input || dataToSave.captainInput;
  dataToSave.captainInput = dataToSave.captainInput || dataToSave.input;
  dataToSave.title =
    dataToSave.title ||
    dataToSave.eventTitle ||
    dataToSave.notificationTitle ||
    '';
  dataToSave.duration_minutes =
    dataToSave.duration_minutes || dataToSave.eventDuration;
  dataToSave.eventDuration =
    dataToSave.eventDuration || dataToSave.duration_minutes;
  dataToSave.frequency = dataToSave.frequency || dataToSave.schedule;
  dataToSave.stage = dataToSave.stage || dataToSave.stageId;
  dataToSave.stageId = dataToSave.stageId || dataToSave.stage;

  // ─── Fix field name mismatches between frontend and backend ─────────────

  // 1. send_whatsapp_template: frontend saves content, backend reads template_name
  if (dataToSave.type === 'send_whatsapp_template') {
    dataToSave.template_name =
      dataToSave.content || dataToSave.templateName || '';
    dataToSave.template = dataToSave.template_name;
  }

  // 2. send_attachment: frontend saves attachmentUrl, backend reads file_url/url
  dataToSave.file_url =
    dataToSave.attachmentUrl || dataToSave.file_url || dataToSave.url || '';

  // 3. send_email_to_team: frontend saves teamId (scalar), backend reads team_ids (Array)
  if (dataToSave.teamId) {
    dataToSave.team_ids = [dataToSave.teamId];
  }

  // 4. send_email_transcript: frontend saves emailAddress, backend reads email
  dataToSave.email = dataToSave.emailAddress || dataToSave.email || '';
  dataToSave.recipient = dataToSave.email;

  // 5. snooze_conversation: frontend saves snoozeDuration/snoozeUnit, backend reads duration/unit
  if (dataToSave.snoozeDuration) {
    dataToSave.duration = dataToSave.snoozeDuration;
  }
  if (dataToSave.snoozeUnit) {
    dataToSave.unit = dataToSave.snoozeUnit;
  }

  // 6. transfer_to_inbox: frontend saves target, backend reads inbox_id/inbox_name/inbox
  dataToSave.inbox_id = dataToSave.target || dataToSave.inbox_id || '';
  dataToSave.inbox_name = dataToSave.target || dataToSave.inbox_name || '';
  dataToSave.inbox = dataToSave.target || dataToSave.inbox || '';

  // 7. create_pipeline_card: frontend saves cardPipelineId/cardTitle, backend reads pipeline_id/title
  if (dataToSave.cardPipelineId) {
    dataToSave.pipeline_id = dataToSave.cardPipelineId;
  }
  if (dataToSave.cardTitle) {
    dataToSave.title = dataToSave.cardTitle;
  }

  // 8. create_task: frontend saves taskTitle/taskDescription/taskDueDate, backend reads title/description/due_at
  if (dataToSave.taskTitle) {
    dataToSave.title = dataToSave.taskTitle;
  }
  if (dataToSave.taskDescription) {
    dataToSave.description = dataToSave.taskDescription;
  }
  if (dataToSave.taskDueDate) {
    dataToSave.due_at = dataToSave.taskDueDate;
  }

  // 9. add_tag: frontend saves tag (single string), backend reads key+value
  if (dataToSave.tag) {
    dataToSave.key = dataToSave.tag;
    dataToSave.value = dataToSave.tag;
  }

  // 10. set_conversation_attribute: frontend saves attributeKey+attributeValue, backend reads attributes hash
  if (dataToSave.attributeKey && dataToSave.attributeValue !== undefined) {
    dataToSave.attributes = {
      [dataToSave.attributeKey]: dataToSave.attributeValue,
    };
  }

  // 11. update_contact: frontend saves contactField+contactValue, backend reads field name directly
  if (dataToSave.contactField && dataToSave.contactValue !== undefined) {
    dataToSave[dataToSave.contactField] = dataToSave.contactValue;
  }

  // 12. call_captain_ai: frontend saves captainInput, backend reads prompt
  dataToSave.prompt =
    dataToSave.captainInput || dataToSave.prompt || dataToSave.input || '';

  // 13. create_google_event: eventDescription not synced to description
  dataToSave.description =
    dataToSave.eventDescription || dataToSave.description || '';

  // 14. send_whatsapp_media: mediaUrl (camelCase) -> media_url (snake_case)
  dataToSave.media_url = dataToSave.mediaUrl || dataToSave.media_url || '';

  const persistedData =
    nodeType.value === 'trigger'
      ? normalizeTriggerFilterData(dataToSave)
      : dataToSave;

  persistedData.label = buildLabel(resolvedType, persistedData);

  store.dispatch('automationFlow/updateNodeData', {
    nodeId: props.nodeId,
    data: persistedData,
  });

  initialDraftSnapshot.value = captureDraftSnapshot();
  emit('save');
  close();
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const inputClass =
  'w-full min-h-11 !mb-0 p-2 border border-n-weak rounded-md bg-n-background text-n-slate-12 text-sm focus:outline-none focus:ring-1 focus:ring-n-blue-6';

const errorInputClass =
  'w-full min-h-11 !mb-0 p-2 border border-n-ruby-6 rounded-md bg-n-background text-n-slate-12 text-sm focus:outline-none focus:ring-1 focus:ring-n-ruby-6';

const fieldClass = field =>
  errors.value[field] ? errorInputClass : inputClass;
</script>

<template>
  <div
    role="dialog"
    :aria-labelledby="`${editorId}-title`"
    class="flex flex-col h-full bg-n-solid-2 border-l border-n-weak overflow-hidden [&_button]:min-h-11 [&_select]:bg-[right_0.65rem_center] [&_select]:!pr-10"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-n-weak flex-shrink-0"
    >
      <h2 :id="`${editorId}-title`" class="text-heading-3 text-n-slate-12">
        {{ title }}
      </h2>
      <button
        type="button"
        class="min-w-11 p-1 rounded-md hover:bg-n-alpha-1 text-n-slate-10 transition-colors"
        :aria-label="t('PIPELINE.FLOW_BUILDER.RUN.CLOSE')"
        @click="close"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>

    <!-- Scrollable form content -->
    <div class="flex-1 overflow-y-auto p-4">
      <!-- ══════════════════════════ TRIGGER EDITOR ══════════════════════════ -->
      <div v-if="nodeType === 'trigger'" class="space-y-4">
        <!-- Event type selector -->
        <div>
          <label
            :for="controlId('trigger-event')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.TRIGGER_EVENT_LABEL') }}
          </label>
          <select
            :id="controlId('trigger-event')"
            v-model="localData.eventType"
            :class="inputClass"
          >
            <option value="" disabled>
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.TRIGGER_EVENT_PLACEHOLDER') }}
            </option>
            <option
              v-for="opt in triggerOptions"
              :key="opt.subtype"
              :value="opt.subtype"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>

        <!-- Pipeline selector (shown for item_moved) -->
        <div
          v-if="
            triggerType === 'item_moved' ||
            triggerType === 'stage_changed' ||
            triggerType === 'item_created' ||
            triggerType === 'item_won'
          "
        >
          <label
            :for="controlId('trigger-pipeline')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.TRIGGER_PIPELINE_LABEL') }}
          </label>
          <select
            :id="controlId('trigger-pipeline')"
            v-model="localData.pipelineId"
            :class="inputClass"
          >
            <option value="">
              {{
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.TRIGGER_PIPELINE_PLACEHOLDER',
                  'Todos os pipelines'
                )
              }}
            </option>
            <option
              v-for="pipeline in pipelines"
              :key="pipeline.id"
              :value="pipeline.id"
            >
              {{ pipeline.name }}
            </option>
          </select>
        </div>

        <!-- message_received: keyword filter -->
        <div v-if="triggerType === 'message_received'">
          <label
            :for="controlId('trigger-pattern')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_PATTERN',
                'Filtro de palavras-chave'
              )
            }}
          </label>
          <input
            :id="controlId('trigger-pattern')"
            v-model="localData.pattern"
            type="text"
            :class="inputClass"
            :placeholder="
              t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_PATTERN_EXAMPLE')
            "
          />
          <p class="text-xs text-n-slate-10 mt-1">
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.PATTERN_HINT',
                'Use * como curinga. Separe múltiplos com vírgula. Deixe vazio para todas as mensagens.'
              )
            }}
          </p>
        </div>

        <!-- item_moved: from/to stage dropdowns -->
        <template
          v-if="triggerType === 'item_moved' || triggerType === 'stage_changed'"
        >
          <div>
            <label
              :for="controlId('trigger-from-column')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{
                t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_FROM_COLUMN', 'Da etapa')
              }}
            </label>
            <select
              v-if="triggerPipelineStages.length"
              :id="controlId('trigger-from-column')"
              v-model="localData.fromColumn"
              :class="inputClass"
            >
              <option value="">
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.ANY_STAGE_OPTION') }}
              </option>
              <option
                v-for="stage in triggerPipelineStages"
                :key="stage.id"
                :value="stage.id"
              >
                {{ stage.name }}
              </option>
            </select>
            <input
              v-else
              :id="controlId('trigger-from-column')"
              v-model="localData.fromColumn"
              type="text"
              :class="inputClass"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.STAGE_FROM_PLACEHOLDER')
              "
            />
          </div>
          <div>
            <label
              :for="controlId('trigger-to-column')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_TO_COLUMN',
                  'Para a etapa'
                )
              }}
            </label>
            <select
              v-if="triggerPipelineStages.length"
              :id="controlId('trigger-to-column')"
              v-model="localData.toColumn"
              :class="inputClass"
            >
              <option value="">
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.ANY_STAGE_OPTION') }}
              </option>
              <option
                v-for="stage in triggerPipelineStages"
                :key="stage.id"
                :value="stage.id"
              >
                {{ stage.name }}
              </option>
            </select>
            <input
              v-else
              :id="controlId('trigger-to-column')"
              v-model="localData.toColumn"
              type="text"
              :class="inputClass"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.STAGE_TO_PLACEHOLDER')
              "
            />
          </div>
        </template>

        <!-- scheduled: frequency + time -->
        <template v-if="triggerType === 'scheduled'">
          <div>
            <label
              :for="controlId('trigger-schedule')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{
                t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_SCHEDULE', 'Frequência')
              }}
            </label>
            <select
              :id="controlId('trigger-schedule')"
              v-model="localData.schedule"
              :class="inputClass"
            >
              <option value="daily">
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SCHEDULE_DAILY', 'Diário') }}
              </option>
              <option value="weekly">
                {{
                  t('PIPELINE.FLOW_BUILDER.EDITOR.SCHEDULE_WEEKLY', 'Semanal')
                }}
              </option>
              <option value="monthly">
                {{
                  t('PIPELINE.FLOW_BUILDER.EDITOR.SCHEDULE_MONTHLY', 'Mensal')
                }}
              </option>
            </select>
          </div>
          <div>
            <label
              :for="controlId('trigger-time')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_TIME', 'Horário') }}
            </label>
            <input
              :id="controlId('trigger-time')"
              v-model="localData.time"
              type="time"
              :class="inputClass"
            />
          </div>
        </template>

        <!-- webhook_received: display URL (read-only) -->
        <div v-if="triggerType === 'webhook_received'">
          <p
            :id="controlId('webhook-url-label')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.WEBHOOK_URL_LABEL') }}
          </p>
          <p v-if="!canManageAutomations" class="text-xs text-n-slate-10">
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.WEBHOOK_URL_RESTRICTED') }}
          </p>
          <p
            v-else-if="webhookCredentialsLoading"
            class="text-xs text-n-slate-10"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.WEBHOOK_URL_LOADING') }}
          </p>
          <template v-else-if="webhookUrl">
            <div class="flex gap-2 items-center">
              <input
                :id="controlId('webhook-url')"
                :value="webhookUrl"
                type="text"
                readonly
                :aria-labelledby="controlId('webhook-url-label')"
                class="w-full min-h-11 !mb-0 p-2 border border-n-weak rounded-md bg-n-alpha-1 text-n-slate-11 text-xs font-mono"
              />
              <Button
                variant="ghost"
                color="slate"
                size="sm"
                :disabled="webhookRotationLoading"
                @click="rotateWebhookToken"
              >
                <span
                  class="i-lucide-refresh-cw size-4"
                  :class="{ 'animate-spin': webhookRotationLoading }"
                  aria-hidden="true"
                />
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.WEBHOOK_URL_ROTATE') }}
              </Button>
            </div>
            <p class="text-xs text-n-slate-10 mt-1">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.WEBHOOK_URL_HINT') }}
            </p>
          </template>
          <p v-else-if="webhookCredentialError" class="text-xs text-n-ruby-11">
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.WEBHOOK_URL_UNAVAILABLE') }}
          </p>
          <p v-else class="text-xs text-n-slate-10">
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.WEBHOOK_URL_PENDING') }}
          </p>
        </div>

        <!-- Optional description -->
        <div>
          <label
            :for="controlId('trigger-description')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{
              t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_DESCRIPTION', 'Descrição')
            }}
            <span class="text-n-slate-10 font-normal">{{
              t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_OPTIONAL')
            }}</span>
          </label>
          <input
            :id="controlId('trigger-description')"
            v-model="localData.description"
            type="text"
            :class="inputClass"
            :placeholder="
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.DESCRIPTION_PLACEHOLDER',
                'Descrição opcional...'
              )
            "
          />
        </div>
      </div>

      <!-- ══════════════════════════ CONDITION EDITOR ════════════════════════ -->
      <div v-else-if="nodeType === 'condition'" class="space-y-4">
        <!-- Field -->
        <div>
          <label
            :for="controlId('condition-field')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{
              t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_CONDITION_TYPE', 'Campo')
            }}
          </label>
          <select
            :id="controlId('condition-field')"
            v-model="localData.field"
            :class="fieldClass('field')"
          >
            <option value="" disabled>
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_FIELD_PLACEHOLDER') }}
            </option>
            <optgroup
              v-for="fieldGroup in CONDITION_FIELD_GROUPS"
              :key="fieldGroup.group"
              :label="t(fieldGroup.labelKey)"
            >
              <option
                v-for="field in fieldGroup.fields"
                :key="field.token"
                :value="field.token"
              >
                {{ t(field.labelKey) }}
              </option>
            </optgroup>
          </select>
          <p v-if="errors.field" class="text-xs text-n-ruby-11 mt-1">
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.REQUIRED_FIELD') }}
          </p>
        </div>

        <!-- Operator -->
        <div>
          <label
            :for="controlId('condition-operator')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_OPERATOR', 'Operador') }}
          </label>
          <select
            :id="controlId('condition-operator')"
            v-model="localData.operator"
            :class="inputClass"
          >
            <option v-for="op in availableOperators" :key="op" :value="op">
              {{
                t(`PIPELINE.FLOW_BUILDER.NODE.CONDITION.OPERATORS.${op}`, op)
              }}
            </option>
          </select>
        </div>

        <!-- Value (dynamic based on field type) -->
        <div>
          <label
            :for="controlId('condition-value')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_VALUE', 'Valor') }}
          </label>

          <!-- Status dropdown -->
          <select
            v-if="conditionValueType === 'enum_status'"
            :id="controlId('condition-value')"
            v-model="localData.value"
            :class="fieldClass('value')"
          >
            <option value="" disabled>
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_STATUS_PLACEHOLDER') }}
            </option>
            <option value="open">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.STATUS_OPEN') }}
            </option>
            <option value="resolved">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.STATUS_RESOLVED') }}
            </option>
            <option value="pending">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.STATUS_PENDING') }}
            </option>
            <option value="snoozed">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.STATUS_SNOOZED') }}
            </option>
          </select>

          <!-- Priority dropdown -->
          <select
            v-else-if="conditionValueType === 'enum_priority'"
            :id="controlId('condition-value')"
            v-model="localData.value"
            :class="fieldClass('value')"
          >
            <option value="" disabled>
              {{
                t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_PRIORITY_PLACEHOLDER')
              }}
            </option>
            <option value="high">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.PRIORITY_HIGH') }}
            </option>
            <option value="medium">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.PRIORITY_MEDIUM') }}
            </option>
            <option value="low">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.PRIORITY_LOW') }}
            </option>
            <option value="none">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.PRIORITY_NONE') }}
            </option>
          </select>

          <!-- Agent dropdown -->
          <select
            v-else-if="conditionValueType === 'agent'"
            :id="controlId('condition-value')"
            v-model="localData.value"
            :class="fieldClass('value')"
          >
            <option value="" disabled>
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_AGENT_PLACEHOLDER') }}
            </option>
            <option
              v-for="agent in agents"
              :key="agent.id"
              :value="String(agent.id)"
            >
              {{ agent.name }}
            </option>
          </select>

          <!-- Team dropdown -->
          <select
            v-else-if="conditionValueType === 'team'"
            :id="controlId('condition-value')"
            v-model="localData.value"
            :class="fieldClass('value')"
          >
            <option value="" disabled>
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_TEAM_PLACEHOLDER') }}
            </option>
            <option
              v-for="team in teams"
              :key="team.id"
              :value="String(team.id)"
            >
              {{ team.name }}
            </option>
          </select>

          <!-- Inbox channel dropdown -->
          <select
            v-else-if="conditionValueType === 'channel'"
            :id="controlId('condition-value')"
            v-model="localData.value"
            :class="fieldClass('value')"
          >
            <option value="" disabled>
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_CHANNEL_PLACEHOLDER') }}
            </option>
            <option
              v-for="channelType in channelTypeOptions"
              :key="channelType.value"
              :value="channelType.value"
            >
              {{ channelType.label }}
            </option>
          </select>

          <!-- Text input -->
          <input
            v-else
            :id="controlId('condition-value')"
            v-model="localData.value"
            type="text"
            :class="fieldClass('value')"
            :placeholder="
              t('PIPELINE.FLOW_BUILDER.EDITOR.COMPARE_VALUE_PLACEHOLDER')
            "
          />
          <p v-if="errors.value" class="text-xs text-n-ruby-11 mt-1">
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.REQUIRED_FIELD') }}
          </p>
        </div>

        <!-- Optional description -->
        <div>
          <label
            :for="controlId('condition-description')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_DESCRIPTION') }}
            <span class="text-n-slate-10 font-normal">{{
              t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_OPTIONAL')
            }}</span>
          </label>
          <input
            :id="controlId('condition-description')"
            v-model="localData.description"
            type="text"
            :class="inputClass"
            :placeholder="
              t('PIPELINE.FLOW_BUILDER.EDITOR.DESCRIPTION_PLACEHOLDER')
            "
          />
        </div>
      </div>

      <!-- ══════════════════════════ ACTION EDITOR ═══════════════════════════ -->
      <div v-else-if="nodeType === 'action'" class="space-y-4">
        <!-- send_message / add_note -->
        <template v-if="['send_message', 'add_note'].includes(actionType)">
          <div>
            <div class="flex items-center justify-between mb-1">
              <label
                :for="controlId('action-message')"
                class="block text-label text-n-slate-12"
              >
                {{
                  t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_MESSAGE', 'Mensagem')
                }}
              </label>
              <button
                type="button"
                class="text-xs text-n-blue-11 hover:text-n-blue-12 flex items-center gap-1"
                @click="openVariablePicker('message')"
              >
                <span>{{
                  t('PIPELINE.FLOW_BUILDER.EDITOR.VARIABLES_INSERT')
                }}</span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M12 5v14M5 12l7 7 7-7" />
                </svg>
              </button>
            </div>
            <textarea
              :id="controlId('action-message')"
              v-model="localData.message"
              rows="4"
              :class="fieldClass('message')"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.MESSAGE_VARIABLES_PLACEHOLDER')
              "
            />
            <p v-if="errors.message" class="text-xs text-n-ruby-11 mt-1">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.REQUIRED_MESSAGE') }}
            </p>
          </div>

          <!-- Variable picker popover -->
          <div
            v-if="showVariablePicker && activeVarField === 'message'"
            class="border border-n-weak rounded-lg bg-n-background shadow-md p-3 max-h-48 overflow-y-auto"
          >
            <div
              v-for="category in VARIABLE_CATEGORIES"
              :key="category.label"
              class="mb-2 last:mb-0"
            >
              <p class="text-xs font-semibold text-n-slate-11 mb-1">
                {{ category.label }}
              </p>
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="v in category.variables"
                  :key="v.value"
                  type="button"
                  class="text-xs px-2 py-1 bg-n-blue-3 text-n-blue-11 rounded hover:bg-n-blue-4 border border-n-blue-6"
                  :title="v.value"
                  @click="insertVariable(v.value)"
                >
                  {{ v.label }}
                </button>
              </div>
            </div>
          </div>
        </template>

        <!-- send_notification -->
        <template v-else-if="actionType === 'send_notification'">
          <div>
            <label
              :for="controlId('notification-title')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_NOTIFICATION_TITLE') }}
            </label>
            <input
              :id="controlId('notification-title')"
              v-model="localData.notificationTitle"
              type="text"
              :class="inputClass"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_NOTIFICATION_TITLE')
              "
            />
          </div>
          <div>
            <div class="flex items-center justify-between mb-1">
              <label
                :for="controlId('notification-body')"
                class="block text-label text-n-slate-12"
              >
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_NOTIFICATION_BODY') }}
              </label>
              <button
                type="button"
                class="text-xs text-n-blue-11 hover:text-n-blue-12"
                @click="openVariablePicker('message')"
              >
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.VARIABLES_INSERT') }}
              </button>
            </div>
            <textarea
              :id="controlId('notification-body')"
              v-model="localData.message"
              rows="3"
              :class="fieldClass('message')"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.MESSAGE_VARIABLES_PLACEHOLDER')
              "
            />
            <p v-if="errors.message" class="text-xs text-n-ruby-11 mt-1">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.REQUIRED_MESSAGE') }}
            </p>
            <div
              v-if="showVariablePicker && activeVarField === 'message'"
              class="mt-2 border border-n-weak rounded-lg bg-n-background p-3 max-h-48 overflow-y-auto"
            >
              <div
                v-for="category in VARIABLE_CATEGORIES"
                :key="category.label"
                class="mb-2 last:mb-0"
              >
                <p class="text-xs font-semibold text-n-slate-11 mb-1">
                  {{ category.label }}
                </p>
                <div class="flex flex-wrap gap-1">
                  <button
                    v-for="v in category.variables"
                    :key="v.value"
                    type="button"
                    class="text-xs px-2 py-1 bg-n-blue-3 text-n-blue-11 rounded hover:bg-n-blue-4 border border-n-blue-6"
                    :title="v.value"
                    @click="insertVariable(v.value)"
                  >
                    {{ v.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- assign_agent -->
        <div v-else-if="actionType === 'assign_agent'">
          <label
            :for="controlId('action-agent')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_AGENT_LABEL') }}
          </label>
          <select
            :id="controlId('action-agent')"
            v-model="localData.agentId"
            :class="fieldClass('agentId')"
          >
            <option value="" disabled>
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_AGENT_PLACEHOLDER') }}
            </option>
            <option
              v-for="agent in agents"
              :key="agent.id"
              :value="String(agent.id)"
            >
              {{ agent.name }}
            </option>
          </select>
          <p v-if="errors.agentId" class="text-xs text-n-ruby-11 mt-1">
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.REQUIRED_AGENT') }}
          </p>
        </div>

        <!-- assign_team -->
        <div v-else-if="actionType === 'assign_team'">
          <label
            :for="controlId('action-team')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_TEAM_LABEL') }}
          </label>
          <select
            :id="controlId('action-team')"
            v-model="localData.teamId"
            :class="fieldClass('teamId')"
          >
            <option value="" disabled>
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_TEAM_PLACEHOLDER') }}
            </option>
            <option
              v-for="team in teams"
              :key="team.id"
              :value="String(team.id)"
            >
              {{ team.name }}
            </option>
          </select>
          <p v-if="errors.teamId" class="text-xs text-n-ruby-11 mt-1">
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.REQUIRED_TEAM') }}
          </p>
        </div>

        <!-- add_label / remove_label -->
        <div v-else-if="['add_label', 'remove_label'].includes(actionType)">
          <label
            :for="controlId('action-label')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_LABEL_LABEL') }}
          </label>
          <select
            :id="controlId('action-label')"
            v-model="localData.labelId"
            :class="fieldClass('labelId')"
          >
            <option value="" disabled>
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_LABEL_PLACEHOLDER') }}
            </option>
            <option
              v-for="label in labels"
              :key="label.id"
              :value="label.title"
            >
              {{ label.title }}
            </option>
          </select>
          <p v-if="errors.labelId" class="text-xs text-n-ruby-11 mt-1">
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.REQUIRED_LABEL') }}
          </p>
        </div>

        <!-- change_status -->
        <div v-else-if="actionType === 'change_status'">
          <label
            :for="controlId('action-status')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_STATUS') }}
          </label>
          <select
            :id="controlId('action-status')"
            v-model="localData.status"
            :class="inputClass"
          >
            <option value="open">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.STATUS_OPEN') }}
            </option>
            <option value="resolved">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.STATUS_RESOLVED') }}
            </option>
            <option value="pending">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.STATUS_PENDING') }}
            </option>
            <option value="snoozed">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.STATUS_SNOOZED') }}
            </option>
          </select>
        </div>

        <!-- change_priority -->
        <div v-else-if="actionType === 'change_priority'">
          <label
            :for="controlId('action-priority')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_PRIORITY') }}
          </label>
          <select
            :id="controlId('action-priority')"
            v-model="localData.priority"
            :class="inputClass"
          >
            <option value="high">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.PRIORITY_HIGH') }}
            </option>
            <option value="medium">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.PRIORITY_MEDIUM') }}
            </option>
            <option value="low">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.PRIORITY_LOW') }}
            </option>
            <option value="none">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.PRIORITY_NONE') }}
            </option>
          </select>
        </div>

        <!-- wait -->
        <div v-else-if="actionType === 'wait'">
          <label
            :for="controlId('wait-duration')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_WAIT_DURATION') }}
          </label>
          <div class="flex gap-2">
            <input
              :id="controlId('wait-duration')"
              v-model.number="localData.duration"
              type="number"
              min="1"
              class="w-24 flex-shrink-0"
              :class="fieldClass('duration')"
            />
            <select
              :id="controlId('wait-unit')"
              v-model="localData.unit"
              :class="inputClass"
              :aria-label="t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_UNIT')"
            >
              <option value="minutes">
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.WAIT_MINUTES') }}
              </option>
              <option value="hours">
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.WAIT_HOURS') }}
              </option>
              <option value="days">
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.WAIT_DAYS') }}
              </option>
            </select>
          </div>
          <p v-if="errors.duration" class="text-xs text-n-ruby-11 mt-1">
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.REQUIRED_DURATION') }}
          </p>
        </div>

        <!-- http_request / send_webhook / trigger_webhook -->
        <template
          v-else-if="
            ['http_request', 'send_webhook', 'trigger_webhook'].includes(
              actionType
            )
          "
        >
          <div>
            <label
              :for="controlId('request-url')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_URL') }}
            </label>
            <input
              :id="controlId('request-url')"
              v-model="localData.url"
              type="url"
              :class="fieldClass('url')"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_URL_PLACEHOLDER')
              "
            />
            <p v-if="errors.url" class="text-xs text-n-ruby-11 mt-1">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.REQUIRED_URL') }}
            </p>
          </div>
          <div>
            <label
              :for="controlId('request-method')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_METHOD') }}
            </label>
            <select
              :id="controlId('request-method')"
              v-model="localData.method"
              :class="inputClass"
            >
              <!-- eslint-disable vue/no-bare-strings-in-template -->
              <option>GET</option>
              <option>POST</option>
              <option>PUT</option>
              <option>PATCH</option>
              <option>DELETE</option>
              <!-- eslint-enable vue/no-bare-strings-in-template -->
            </select>
          </div>
          <div>
            <label
              :for="controlId('request-headers')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_HEADERS') }}
            </label>
            <textarea
              :id="controlId('request-headers')"
              v-model="localData.headers"
              rows="2"
              :class="inputClass"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_HEADERS_PLACEHOLDER')
              "
            />
          </div>
          <div>
            <label
              :for="controlId('request-body')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_BODY') }}
            </label>
            <textarea
              :id="controlId('request-body')"
              v-model="localData.body"
              rows="3"
              :class="inputClass"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_BODY_PLACEHOLDER')
              "
            />
          </div>
        </template>

        <!-- call_captain_ai -->
        <template v-else-if="actionType === 'call_captain_ai'">
          <div>
            <label
              :for="controlId('captain-assistant')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_CAPTAIN_ASSISTANT') }}
            </label>
            <select
              :id="controlId('captain-assistant')"
              v-model="localData.captainAssistantId"
              :class="inputClass"
            >
              <option value="" disabled>
                {{
                  t(
                    'PIPELINE.FLOW_BUILDER.EDITOR.CAPTAIN_ASSISTANT_PLACEHOLDER'
                  )
                }}
              </option>
              <option
                v-for="assistant in captainAssistants"
                :key="assistant.id"
                :value="String(assistant.id)"
              >
                {{ assistant.name }}
              </option>
            </select>
          </div>
          <div>
            <div class="flex items-center justify-between mb-1">
              <label
                :for="controlId('captain-input')"
                class="block text-label text-n-slate-12"
              >
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_CAPTAIN_INPUT') }}
              </label>
              <button
                type="button"
                class="text-xs text-n-blue-11 hover:text-n-blue-12"
                @click="openVariablePicker('captainInput')"
              >
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.VARIABLES_INSERT') }}
              </button>
            </div>
            <input
              :id="controlId('captain-input')"
              v-model="localData.captainInput"
              type="text"
              :class="inputClass"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.CAPTAIN_INPUT_PLACEHOLDER')
              "
            />
            <div
              v-if="showVariablePicker && activeVarField === 'captainInput'"
              class="mt-2 border border-n-weak rounded-lg bg-n-background p-3 max-h-48 overflow-y-auto"
            >
              <div
                v-for="category in VARIABLE_CATEGORIES"
                :key="category.label"
                class="mb-2 last:mb-0"
              >
                <p class="text-xs font-semibold text-n-slate-11 mb-1">
                  {{ category.label }}
                </p>
                <div class="flex flex-wrap gap-1">
                  <button
                    v-for="v in category.variables"
                    :key="v.value"
                    type="button"
                    class="text-xs px-2 py-1 bg-n-blue-3 text-n-blue-11 rounded hover:bg-n-blue-4 border border-n-blue-6"
                    :title="v.value"
                    @click="insertVariable(v.value)"
                  >
                    {{ v.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- create_google_event -->
        <template v-else-if="actionType === 'create_google_event'">
          <div>
            <label
              :for="controlId('google-event-title')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_EVENT_TITLE') }}
            </label>
            <input
              :id="controlId('google-event-title')"
              v-model="localData.eventTitle"
              type="text"
              :class="fieldClass('eventTitle')"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_EVENT_TITLE_PLACEHOLDER')
              "
            />
            <p v-if="errors.eventTitle" class="text-xs text-n-ruby-11 mt-1">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.REQUIRED_EVENT_TITLE') }}
            </p>
          </div>
          <div>
            <label
              :for="controlId('google-event-duration')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_EVENT_DURATION') }}
            </label>
            <input
              :id="controlId('google-event-duration')"
              v-model.number="localData.eventDuration"
              type="number"
              min="5"
              :class="inputClass"
            />
          </div>
          <div>
            <label
              :for="controlId('google-event-description')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_EVENT_DESCRIPTION') }}
            </label>
            <textarea
              :id="controlId('google-event-description')"
              v-model="localData.eventDescription"
              rows="2"
              :class="inputClass"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.DESCRIPTION_PLACEHOLDER')
              "
            />
          </div>
        </template>

        <!-- move_to_stage / move_item / move_to_column -->
        <template
          v-else-if="
            ['move_to_stage', 'move_item', 'move_to_column'].includes(
              actionType
            )
          "
        >
          <div>
            <label
              :for="controlId('target-pipeline')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_TARGET_PIPELINE') }}
            </label>
            <select
              :id="controlId('target-pipeline')"
              v-model="localData.targetPipelineId"
              :class="inputClass"
            >
              <option value="" disabled>
                {{
                  t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_PIPELINE_PLACEHOLDER')
                }}
              </option>
              <option
                v-for="pipeline in pipelines"
                :key="pipeline.id"
                :value="pipeline.id"
              >
                {{ pipeline.name }}
              </option>
            </select>
          </div>
          <div>
            <label
              :for="controlId('target-stage')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_TARGET_STAGE') }}
            </label>
            <select
              v-if="actionPipelineStages.length"
              :id="controlId('target-stage')"
              v-model="localData.stageId"
              :class="inputClass"
            >
              <option value="" disabled>
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_STAGE_PLACEHOLDER') }}
              </option>
              <option
                v-for="stage in actionPipelineStages"
                :key="stage.id"
                :value="stage.id"
              >
                {{ stage.name }}
              </option>
            </select>
            <input
              v-else
              :id="controlId('target-stage')"
              v-model="localData.stageId"
              type="text"
              :class="inputClass"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.STAGE_SELECT_PIPELINE_FIRST')
              "
              :disabled="!localData.targetPipelineId"
            />
          </div>
        </template>

        <!-- transfer_to_inbox -->
        <div v-else-if="actionType === 'transfer_to_inbox'">
          <label
            :for="controlId('target-inbox')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_INBOX') }}
          </label>
          <select
            v-if="inboxes.length"
            :id="controlId('target-inbox')"
            v-model="localData.target"
            :class="inputClass"
          >
            <option value="" disabled>
              {{
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.SELECT_INBOX_PLACEHOLDER',
                  'Select an inbox...'
                )
              }}
            </option>
            <option
              v-for="inbox in inboxes"
              :key="inbox.id"
              :value="String(inbox.id)"
            >
              {{ inbox.name }}
            </option>
          </select>
          <input
            v-else
            :id="controlId('target-inbox')"
            v-model="localData.target"
            type="text"
            :class="inputClass"
            :placeholder="
              t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_INBOX_PLACEHOLDER')
            "
          />
        </div>

        <!-- send_whatsapp_template -->
        <div v-else-if="actionType === 'send_whatsapp_template'">
          <label
            :for="controlId('whatsapp-template')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_WHATSAPP_TEMPLATE_NAME',
                'Template Name'
              )
            }}
          </label>
          <input
            :id="controlId('whatsapp-template')"
            v-model="localData.content"
            type="text"
            :class="inputClass"
            :placeholder="
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.WHATSAPP_TEMPLATE_PLACEHOLDER',
                'e.g. order_confirmation'
              )
            "
          />
          <p class="text-xs text-n-slate-10 mt-1">
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.WHATSAPP_TEMPLATE_HINT',
                'Enter the exact template name registered in your WhatsApp Business account.'
              )
            }}
          </p>
        </div>

        <!-- send_whatsapp_media -->
        <div v-else-if="actionType === 'send_whatsapp_media'">
          <label
            :for="controlId('whatsapp-media-url')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_MEDIA_URL', 'Media URL') }}
          </label>
          <input
            :id="controlId('whatsapp-media-url')"
            v-model="localData.mediaUrl"
            type="url"
            :class="inputClass"
            :placeholder="
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.MEDIA_URL_PLACEHOLDER',
                'https://example.com/image.jpg'
              )
            "
          />
          <p class="text-xs text-n-slate-10 mt-1">
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.MEDIA_URL_HINT',
                'Publicly accessible URL of the media file (image, video, document).'
              )
            }}
          </p>
          <div class="mt-3">
            <label
              :for="controlId('whatsapp-media-caption')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{
                t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_MEDIA_CAPTION', 'Caption')
              }}
              <span class="text-n-slate-10 font-normal">
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_OPTIONAL') }}
              </span>
            </label>
            <input
              :id="controlId('whatsapp-media-caption')"
              v-model="localData.message"
              type="text"
              :class="inputClass"
              :placeholder="
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.MEDIA_CAPTION_PLACEHOLDER',
                  'Optional caption...'
                )
              "
            />
          </div>
        </div>

        <!-- send_attachment -->
        <div v-else-if="actionType === 'send_attachment'">
          <label
            :for="controlId('attachment-url')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_ATTACHMENT_URL',
                'Attachment URL'
              )
            }}
          </label>
          <input
            :id="controlId('attachment-url')"
            v-model="localData.attachmentUrl"
            type="url"
            :class="inputClass"
            :placeholder="
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.ATTACHMENT_URL_PLACEHOLDER',
                'https://example.com/file.pdf'
              )
            "
          />
          <p class="text-xs text-n-slate-10 mt-1">
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.ATTACHMENT_URL_HINT',
                'Publicly accessible URL of the file to send.'
              )
            }}
          </p>
        </div>

        <!-- send_email_to_team -->
        <template v-else-if="actionType === 'send_email_to_team'">
          <div>
            <label
              :for="controlId('email-team')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_TEAM_LABEL') }}
            </label>
            <select
              :id="controlId('email-team')"
              v-model="localData.teamId"
              :class="inputClass"
            >
              <option value="" disabled>
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_TEAM_PLACEHOLDER') }}
              </option>
              <option
                v-for="team in teams"
                :key="team.id"
                :value="String(team.id)"
              >
                {{ team.name }}
              </option>
            </select>
          </div>
          <div>
            <label
              :for="controlId('email-subject')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{
                t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_EMAIL_SUBJECT', 'Subject')
              }}
            </label>
            <input
              :id="controlId('email-subject')"
              v-model="localData.subject"
              type="text"
              :class="inputClass"
              :placeholder="
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.EMAIL_SUBJECT_PLACEHOLDER',
                  'Email subject...'
                )
              "
            />
          </div>
          <div>
            <div class="flex items-center justify-between mb-1">
              <label
                :for="controlId('email-body')"
                class="block text-label text-n-slate-12"
              >
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_EMAIL_BODY', 'Body') }}
              </label>
              <button
                type="button"
                class="text-xs text-n-blue-11 hover:text-n-blue-12"
                @click="openVariablePicker('message')"
              >
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.VARIABLES_INSERT') }}
              </button>
            </div>
            <textarea
              :id="controlId('email-body')"
              v-model="localData.message"
              rows="4"
              :class="inputClass"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.MESSAGE_VARIABLES_PLACEHOLDER')
              "
            />
            <div
              v-if="showVariablePicker && activeVarField === 'message'"
              class="mt-2 border border-n-weak rounded-lg bg-n-background p-3 max-h-48 overflow-y-auto"
            >
              <div
                v-for="category in VARIABLE_CATEGORIES"
                :key="category.label"
                class="mb-2 last:mb-0"
              >
                <p class="text-xs font-semibold text-n-slate-11 mb-1">
                  {{ category.label }}
                </p>
                <div class="flex flex-wrap gap-1">
                  <button
                    v-for="v in category.variables"
                    :key="v.value"
                    type="button"
                    class="text-xs px-2 py-1 bg-n-blue-3 text-n-blue-11 rounded hover:bg-n-blue-4 border border-n-blue-6"
                    :title="v.value"
                    @click="insertVariable(v.value)"
                  >
                    {{ v.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- send_email_transcript -->
        <div v-else-if="actionType === 'send_email_transcript'">
          <label
            :for="controlId('transcript-email')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_EMAIL_ADDRESS',
                'Email Address'
              )
            }}
          </label>
          <input
            :id="controlId('transcript-email')"
            v-model="localData.emailAddress"
            type="email"
            :class="inputClass"
            :placeholder="
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.EMAIL_ADDRESS_PLACEHOLDER',
                `recipient{'@'}example.com`
              )
            "
          />
          <p class="text-xs text-n-slate-10 mt-1">
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.EMAIL_TRANSCRIPT_HINT',
                'The conversation transcript will be sent to this email address.'
              )
            }}
          </p>
        </div>

        <!-- send_csat_survey -->
        <div
          v-else-if="actionType === 'send_csat_survey'"
          class="rounded-lg border border-n-weak bg-n-alpha-1 p-4"
        >
          <p class="text-sm text-n-slate-12 font-medium mb-1">
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.CSAT_SURVEY_TITLE',
                'Send CSAT Survey'
              )
            }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.CSAT_SURVEY_HINT',
                'A customer satisfaction survey will be sent automatically to the contact when this action runs.'
              )
            }}
          </p>
        </div>

        <!-- resolve_conversation / reopen_conversation / mute_conversation / unmute_conversation -->
        <div
          v-else-if="
            [
              'resolve_conversation',
              'reopen_conversation',
              'mute_conversation',
              'unmute_conversation',
            ].includes(actionType)
          "
          class="rounded-lg border border-n-weak bg-n-alpha-1 p-4"
        >
          <p class="text-sm text-n-slate-12 font-medium mb-1">
            {{
              t(`PIPELINE.FLOW_BUILDER.NODE.ACTION.${actionType}`, actionType)
            }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{
              t(
                `PIPELINE.FLOW_BUILDER.EDITOR.ACTION_CONFIRM_${actionType.toUpperCase()}`,
                'This action will be applied to the conversation when the flow runs.'
              )
            }}
          </p>
        </div>

        <!-- snooze_conversation -->
        <div v-else-if="actionType === 'snooze_conversation'">
          <label
            :for="controlId('snooze-duration')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_SNOOZE_DURATION',
                'Snooze Duration'
              )
            }}
          </label>
          <div class="flex gap-2">
            <input
              :id="controlId('snooze-duration')"
              v-model.number="localData.snoozeDuration"
              type="number"
              min="1"
              class="w-24 flex-shrink-0"
              :class="inputClass"
            />
            <select
              :id="controlId('snooze-unit')"
              v-model="localData.snoozeUnit"
              :class="inputClass"
              :aria-label="t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_UNIT')"
            >
              <option value="minutes">
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.WAIT_MINUTES', 'Minutes') }}
              </option>
              <option value="hours">
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.WAIT_HOURS', 'Hours') }}
              </option>
              <option value="days">
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.WAIT_DAYS', 'Days') }}
              </option>
            </select>
          </div>
          <p class="text-xs text-n-slate-10 mt-1">
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.SNOOZE_HINT',
                'The conversation will be snoozed for this duration before resurfacing.'
              )
            }}
          </p>
        </div>

        <!-- set_conversation_attribute / set_attribute -->
        <template
          v-else-if="
            ['set_conversation_attribute', 'set_attribute'].includes(actionType)
          "
        >
          <div>
            <label
              :for="controlId('attribute-key')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_ATTRIBUTE_KEY',
                  'Attribute Key'
                )
              }}
            </label>
            <input
              :id="controlId('attribute-key')"
              v-model="localData.attributeKey"
              type="text"
              :class="inputClass"
              :placeholder="
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.ATTRIBUTE_KEY_PLACEHOLDER',
                  'e.g. priority_level'
                )
              "
            />
          </div>
          <div>
            <label
              :for="controlId('attribute-value')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_ATTRIBUTE_VALUE',
                  'Attribute Value'
                )
              }}
            </label>
            <input
              :id="controlId('attribute-value')"
              v-model="localData.attributeValue"
              type="text"
              :class="inputClass"
              :placeholder="
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.ATTRIBUTE_VALUE_PLACEHOLDER',
                  'Value...'
                )
              "
            />
          </div>
        </template>

        <!-- update_contact -->
        <template v-else-if="actionType === 'update_contact'">
          <div>
            <label
              :for="controlId('contact-field')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_CONTACT_FIELD',
                  'Field to Update'
                )
              }}
            </label>
            <select
              :id="controlId('contact-field')"
              v-model="localData.contactField"
              :class="inputClass"
            >
              <option value="" disabled>
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_FIELD_PLACEHOLDER') }}
              </option>
              <option value="name">
                {{
                  t('PIPELINE.FLOW_BUILDER.EDITOR.CONTACT_FIELD_NAME', 'Name')
                }}
              </option>
              <option value="email">
                {{
                  t('PIPELINE.FLOW_BUILDER.EDITOR.CONTACT_FIELD_EMAIL', 'Email')
                }}
              </option>
              <option value="phone_number">
                {{
                  t('PIPELINE.FLOW_BUILDER.EDITOR.CONTACT_FIELD_PHONE', 'Phone')
                }}
              </option>
              <option value="company_name">
                {{
                  t(
                    'PIPELINE.FLOW_BUILDER.EDITOR.CONTACT_FIELD_COMPANY',
                    'Company'
                  )
                }}
              </option>
              <option value="custom_attribute">
                {{
                  t(
                    'PIPELINE.FLOW_BUILDER.EDITOR.CONTACT_FIELD_CUSTOM',
                    'Custom Attribute'
                  )
                }}
              </option>
            </select>
          </div>
          <div>
            <label
              :for="controlId('contact-value')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_CONTACT_VALUE',
                  'New Value'
                )
              }}
            </label>
            <input
              :id="controlId('contact-value')"
              v-model="localData.contactValue"
              type="text"
              :class="inputClass"
              :placeholder="
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.CONTACT_VALUE_PLACEHOLDER',
                  'New value or {{variable}}...'
                )
              "
            />
          </div>
        </template>

        <!-- create_pipeline_card -->
        <template v-else-if="actionType === 'create_pipeline_card'">
          <div>
            <label
              :for="controlId('card-pipeline')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_TARGET_PIPELINE') }}
            </label>
            <select
              :id="controlId('card-pipeline')"
              v-model="localData.cardPipelineId"
              :class="inputClass"
            >
              <option value="" disabled>
                {{
                  t('PIPELINE.FLOW_BUILDER.EDITOR.SELECT_PIPELINE_PLACEHOLDER')
                }}
              </option>
              <option
                v-for="pipeline in pipelines"
                :key="pipeline.id"
                :value="pipeline.id"
              >
                {{ pipeline.name }}
              </option>
            </select>
          </div>
          <div>
            <label
              :for="controlId('card-title')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{
                t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_CARD_TITLE', 'Card Title')
              }}
            </label>
            <input
              :id="controlId('card-title')"
              v-model="localData.cardTitle"
              type="text"
              :class="inputClass"
              :placeholder="
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.CARD_TITLE_PLACEHOLDER',
                  'e.g. Follow-up with {{contact.name}}'
                )
              "
            />
          </div>
        </template>

        <!-- create_task -->
        <template v-else-if="actionType === 'create_task'">
          <div>
            <label
              :for="controlId('task-title')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{
                t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_TASK_TITLE', 'Task Title')
              }}
            </label>
            <input
              :id="controlId('task-title')"
              v-model="localData.taskTitle"
              type="text"
              :class="inputClass"
              :placeholder="
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.TASK_TITLE_PLACEHOLDER',
                  'e.g. Call {{contact.name}}'
                )
              "
            />
          </div>
          <div>
            <label
              :for="controlId('task-description')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_TASK_DESCRIPTION',
                  'Description'
                )
              }}
              <span class="text-n-slate-10 font-normal">
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_OPTIONAL') }}
              </span>
            </label>
            <textarea
              :id="controlId('task-description')"
              v-model="localData.taskDescription"
              rows="2"
              :class="inputClass"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.DESCRIPTION_PLACEHOLDER')
              "
            />
          </div>
          <div>
            <label
              :for="controlId('task-due-date')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{
                t(
                  'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_TASK_DUE_DATE',
                  'Due Date'
                )
              }}
              <span class="text-n-slate-10 font-normal">
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_OPTIONAL') }}
              </span>
            </label>
            <input
              :id="controlId('task-due-date')"
              v-model="localData.taskDueDate"
              type="datetime-local"
              :class="inputClass"
            />
          </div>
        </template>

        <!-- add_tag -->
        <div v-else-if="actionType === 'add_tag'">
          <label
            :for="controlId('tag')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_TAG', 'Tag') }}
          </label>
          <input
            :id="controlId('tag')"
            v-model="localData.tag"
            type="text"
            :class="inputClass"
            :placeholder="
              t('PIPELINE.FLOW_BUILDER.EDITOR.TAG_PLACEHOLDER', 'e.g. hot-lead')
            "
          />
          <p class="text-xs text-n-slate-10 mt-1">
            {{
              t(
                'PIPELINE.FLOW_BUILDER.EDITOR.TAG_HINT',
                'Enter a single tag to add to the contact. Use lowercase with hyphens.'
              )
            }}
          </p>
        </div>

        <!-- set_variable: dynamic list of {name, value} pairs.
             Matches SetVariableAction backend; up to 20 vars/node. -->
        <template v-else-if="actionType === 'set_variable'">
          <div class="space-y-2">
            <p class="text-[12px] text-n-slate-10">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SET_VARIABLE_HINT') }}
            </p>

            <div
              v-for="(pair, idx) in setVariableEntries"
              :key="idx"
              class="flex items-start gap-2 p-2 bg-n-alpha-1 rounded border border-n-weak"
            >
              <div class="flex-1 space-y-1">
                <input
                  :id="controlId(`variable-name-${idx}`)"
                  v-model="pair.name"
                  type="text"
                  :class="inputClass"
                  :placeholder="
                    t(
                      'PIPELINE.FLOW_BUILDER.EDITOR.SET_VARIABLE_NAME_PLACEHOLDER'
                    )
                  "
                  :aria-label="
                    t(
                      'PIPELINE.FLOW_BUILDER.EDITOR.SET_VARIABLE_NAME_PLACEHOLDER'
                    )
                  "
                  @input="syncSetVariableEntries"
                />
                <input
                  :id="controlId(`variable-value-${idx}`)"
                  v-model="pair.value"
                  type="text"
                  :class="inputClass"
                  :placeholder="
                    t(
                      'PIPELINE.FLOW_BUILDER.EDITOR.SET_VARIABLE_VALUE_PLACEHOLDER'
                    )
                  "
                  :aria-label="
                    t(
                      'PIPELINE.FLOW_BUILDER.EDITOR.SET_VARIABLE_VALUE_PLACEHOLDER'
                    )
                  "
                  @input="syncSetVariableEntries"
                />
              </div>
              <button
                v-if="setVariableEntries.length > 1"
                type="button"
                class="min-w-11 flex items-center justify-center rounded text-n-ruby-9 hover:bg-n-ruby-3"
                :aria-label="t('PIPELINE.FLOW_BUILDER.NODE.MENU.DELETE')"
                @click="removeSetVariableEntry(idx)"
              >
                <span class="i-lucide-x size-4" aria-hidden="true" />
              </button>
            </div>

            <button
              type="button"
              class="flex items-center gap-1 text-xs text-n-blue-11 hover:text-n-blue-12 font-medium"
              :disabled="setVariableEntries.length >= 20"
              @click="addSetVariableEntry"
            >
              <span class="i-lucide-plus size-3.5" aria-hidden="true" />
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SET_VARIABLE_ADD') }}
            </button>
          </div>
        </template>

        <!-- http_response: sets the synchronous response payload that the
             webhook caller receives when the flow finishes. -->
        <template v-else-if="actionType === 'http_response'">
          <div class="space-y-3">
            <p class="text-[12px] text-n-slate-10">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.HTTP_RESPONSE_HINT') }}
            </p>

            <div>
              <label
                :for="controlId('http-response-status')"
                class="block text-label text-n-slate-12 mb-1"
              >
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.HTTP_RESPONSE_STATUS') }}
              </label>
              <input
                :id="controlId('http-response-status')"
                v-model.number="localData.status"
                type="number"
                min="100"
                max="599"
                :class="inputClass"
                :placeholder="
                  t(
                    'PIPELINE.FLOW_BUILDER.EDITOR.HTTP_RESPONSE_STATUS_PLACEHOLDER'
                  )
                "
              />
            </div>

            <div>
              <label
                :for="controlId('http-response-body')"
                class="block text-label text-n-slate-12 mb-1"
              >
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.HTTP_RESPONSE_BODY') }}
              </label>
              <textarea
                :id="controlId('http-response-body')"
                v-model="localData.bodyJson"
                rows="6"
                class="font-mono text-[12px]"
                :class="[
                  httpResponseBodyParseError ? errorInputClass : inputClass,
                ]"
                :placeholder="httpResponseBodyPlaceholder"
                :aria-invalid="!!httpResponseBodyParseError"
                :aria-describedby="
                  httpResponseBodyParseError
                    ? controlId('http-response-body-error')
                    : undefined
                "
              />
              <p
                v-if="httpResponseBodyParseError"
                :id="controlId('http-response-body-error')"
                class="text-[11px] text-n-ruby-11 mt-1"
                role="alert"
              >
                {{ httpResponseBodyParseError }}
              </p>
            </div>

            <div>
              <label
                :for="controlId('http-response-headers')"
                class="block text-label text-n-slate-12 mb-1"
              >
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.HTTP_RESPONSE_HEADERS') }}
                <span class="text-n-slate-10 font-normal">
                  {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_OPTIONAL') }}
                </span>
              </label>
              <textarea
                :id="controlId('http-response-headers')"
                v-model="localData.headersJson"
                rows="3"
                class="font-mono text-[12px]"
                :class="[
                  httpResponseHeadersParseError ? errorInputClass : inputClass,
                ]"
                :placeholder="httpResponseHeadersPlaceholder"
                :aria-invalid="!!httpResponseHeadersParseError"
                :aria-describedby="
                  httpResponseHeadersParseError
                    ? controlId('http-response-headers-error')
                    : undefined
                "
              />
              <p
                v-if="httpResponseHeadersParseError"
                :id="controlId('http-response-headers-error')"
                class="text-[11px] text-n-ruby-11 mt-1"
                role="alert"
              >
                {{ httpResponseHeadersParseError }}
              </p>
            </div>
          </div>
        </template>

        <!-- fallback for unhandled action types -->
        <div v-else-if="actionType === 'call_automation'" class="space-y-3">
          <div>
            <label
              :for="controlId('called-automation')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.CALL_AUTOMATION_LABEL') }}
            </label>
            <select
              :id="controlId('called-automation')"
              v-model="localData.automationId"
              :class="fieldClass('automationId')"
            >
              <option value="" disabled>
                {{
                  t(
                    'PIPELINE.FLOW_BUILDER.EDITOR.SELECT_AUTOMATION_PLACEHOLDER'
                  )
                }}
              </option>
              <option
                v-for="automation in automations"
                :key="automation.id"
                :value="String(automation.id)"
              >
                {{ automation.name }}
              </option>
            </select>
            <p v-if="!automations.length" class="text-xs text-n-slate-10 mt-1">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.CALL_AUTOMATION_EMPTY') }}
            </p>
          </div>
          <label
            :for="controlId('pass-variables')"
            class="flex min-h-11 items-center gap-2 cursor-pointer select-none"
          >
            <input
              :id="controlId('pass-variables')"
              v-model="localData.passVariables"
              type="checkbox"
              class="rounded border-n-weak text-n-brand focus:ring-n-brand"
            />
            <span class="text-label text-n-slate-12">
              {{
                t('PIPELINE.FLOW_BUILDER.EDITOR.CALL_AUTOMATION_PASS_VARIABLES')
              }}
            </span>
          </label>
        </div>

        <div v-else>
          <label
            :for="controlId('fallback-description')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_DESCRIPTION') }}
          </label>
          <textarea
            :id="controlId('fallback-description')"
            v-model="localData.description"
            rows="3"
            :class="inputClass"
            :placeholder="
              t('PIPELINE.FLOW_BUILDER.EDITOR.DESCRIPTION_PLACEHOLDER')
            "
          />
        </div>

        <!-- Optional description (for actions that have a form) -->
        <div
          v-if="
            ![
              'transfer_to_inbox',
              'move_to_stage',
              'move_item',
              'move_to_column',
            ].includes(actionType) && nodeType === 'action'
          "
        >
          <label
            :for="controlId('action-description')"
            class="block text-label text-n-slate-12 mb-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_DESCRIPTION') }}
            <span class="text-n-slate-10 font-normal">{{
              t('PIPELINE.FLOW_BUILDER.EDITOR.FIELD_OPTIONAL')
            }}</span>
          </label>
          <input
            :id="controlId('action-description')"
            v-model="localData.description"
            type="text"
            :class="inputClass"
            :placeholder="
              t('PIPELINE.FLOW_BUILDER.EDITOR.DESCRIPTION_PLACEHOLDER')
            "
          />
        </div>
      </div>
      <!-- switch: edit input expression + dynamic case list -->
      <div v-else-if="nodeType === 'switch'" class="space-y-4">
        <div class="space-y-3">
          <p class="text-[12px] text-n-slate-10">
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SWITCH_HINT') }}
          </p>

          <div>
            <label
              :for="controlId('switch-input')"
              class="block text-label text-n-slate-12 mb-1"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SWITCH_INPUT_FIELD') }}
            </label>
            <input
              :id="controlId('switch-input')"
              v-model="localData.input"
              type="text"
              :class="inputClass"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.EDITOR.SWITCH_INPUT_PLACEHOLDER')
              "
            />
          </div>

          <div class="space-y-2">
            <p class="block text-label text-n-slate-12">
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SWITCH_CASES') }}
            </p>
            <div
              v-for="(c, idx) in switchCases"
              :key="c.id || idx"
              class="flex items-start gap-2 p-2 bg-n-alpha-1 rounded border border-n-weak"
            >
              <div class="flex-1 grid grid-cols-2 gap-2">
                <select
                  :id="controlId(`switch-operator-${idx}`)"
                  v-model="c.operator"
                  :class="inputClass"
                  :aria-label="`${t(
                    'PIPELINE.FLOW_BUILDER.EDITOR.SWITCH_CASES'
                  )} ${idx + 1}: ${t(
                    'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_OPERATOR'
                  )}`"
                  @change="syncSwitchCases"
                >
                  <option v-for="op in SWITCH_OPERATORS" :key="op" :value="op">
                    {{
                      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
                      t(`PIPELINE.FLOW_BUILDER.NODE.CONDITION.OPERATORS.${op}`)
                    }}
                  </option>
                </select>
                <input
                  :id="controlId(`switch-value-${idx}`)"
                  v-model="c.value"
                  type="text"
                  :class="inputClass"
                  :placeholder="
                    t(
                      'PIPELINE.FLOW_BUILDER.EDITOR.SWITCH_CASE_VALUE_PLACEHOLDER'
                    )
                  "
                  :aria-label="`${t(
                    'PIPELINE.FLOW_BUILDER.EDITOR.SWITCH_CASES'
                  )} ${idx + 1}: ${t(
                    'PIPELINE.FLOW_BUILDER.EDITOR.FIELD_VALUE'
                  )}`"
                  @input="syncSwitchCases"
                />
              </div>
              <button
                v-if="switchCases.length > 1"
                type="button"
                class="min-w-11 flex items-center justify-center rounded text-n-ruby-9 hover:bg-n-ruby-3"
                :aria-label="t('PIPELINE.FLOW_BUILDER.NODE.MENU.DELETE')"
                @click="removeSwitchCase(idx)"
              >
                <span class="i-lucide-x size-4" aria-hidden="true" />
              </button>
            </div>

            <button
              type="button"
              class="flex items-center gap-1 text-xs text-n-blue-11 hover:text-n-blue-12 font-medium"
              :disabled="switchCases.length >= 20"
              @click="addSwitchCase"
            >
              <span class="i-lucide-plus size-3.5" aria-hidden="true" />
              {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SWITCH_ADD_CASE') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="flex justify-end gap-2 px-4 py-3 border-t border-n-weak flex-shrink-0"
    >
      <Button variant="ghost" color="slate" @click="close">
        {{ t('PIPELINE.FLOW_BUILDER.EDITOR.CANCEL') }}
      </Button>
      <Button variant="solid" color="blue" @click="saveChanges">
        {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SAVE') }}
      </Button>
    </div>
  </div>

  <ConfirmModal
    ref="discardConfirmation"
    :title="t('PIPELINE.FLOW_BUILDER.CANCEL_TITLE')"
    :description="t('PIPELINE.FLOW_BUILDER.CANCEL_CONFIRM')"
    :confirm-label="t('PIPELINE.FLOW_BUILDER.CANCEL_DISCARD')"
    :cancel-label="t('PIPELINE.FLOW_BUILDER.CANCEL_KEEP_EDITING')"
  />
  <ConfirmModal
    ref="webhookRotationConfirmation"
    :title="t('PIPELINE.FLOW_BUILDER.EDITOR.WEBHOOK_URL_ROTATE_TITLE')"
    :description="t('PIPELINE.FLOW_BUILDER.EDITOR.WEBHOOK_URL_ROTATE_CONFIRM')"
    :confirm-label="t('PIPELINE.FLOW_BUILDER.EDITOR.WEBHOOK_URL_ROTATE_ACTION')"
    :cancel-label="t('PIPELINE.FLOW_BUILDER.EDITOR.CANCEL')"
  />
</template>
